/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { IndianRupee } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type Invoice } from '../../lib/api/finance.js';
import { usePayInvoice } from '../../lib/queries/finance.js';
import { formatInr } from '../../lib/utils/money.js';

interface PayNowModalProps {
  readonly invoice: Invoice | null;
  readonly onClose: () => void;
}

/**
 * Parent pay-now confirmation (P0-3). Settles the invoice's outstanding balance
 * through the mock Razorpay gateway, then closes on success.
 */
export function PayNowModal({ invoice, onClose }: PayNowModalProps): JSX.Element {
  const { t } = useTranslation();
  const pay = usePayInvoice();
  const outstanding = invoice !== null ? invoice.amountPaise - invoice.amountPaidPaise : 0;

  const onPay = useCallback(() => {
    if (invoice !== null) {
      pay.mutate(invoice.id, { onSuccess: onClose });
    }
  }, [pay, invoice, onClose]);

  return (
    <Modal
      open={invoice !== null}
      onOpenChange={onClose}
      title={t('parent.billing.payNowTitle')}
      description={t('parent.billing.payNowSubtitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {invoice?.invoiceNumber ?? ''}
          </p>
          <p className="text-2xl font-bold text-slate-800 dark:text-white">
            {formatInr(outstanding)}
          </p>
        </div>
        {pay.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('parent.billing.payError')}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={onPay} disabled={pay.isPending}>
            <IndianRupee className="h-4 w-4" aria-hidden="true" />
            {pay.isPending ? t('parent.billing.paying') : t('parent.billing.payConfirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
